import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { HeatingCircuit } from 'z3k-config';
import { MaterialBoolControl, MaterialDropdown, NullableNumberInput, StrictNumberInput, TimeFieldInput, } from 'shared/components/fields';
import { Control } from 'shared/components/fields/control';
import { SettingCard, SettingFullRow, } from 'z3k-settings/components/material-utils/settings-card';
import { WeekdaysToggles } from 'console/climatic/setting-tabs/circuit/weekdays-toggles';
import { useHeatingCircuitsItemState } from 'z3k-settings/climatic/heating-circuits-item/lib/useHeatingCircuitsItemState';
import { useEditingProps } from 'z3k-settings/components/utils/unpack-props';
import { parseFloatWithComma, removeNulls } from 'utils';
import { useCallback, useContext } from 'react';
import { heatRequestOptions } from 'z3k-settings/components/heating-circuits/heat-request';
import { Z3KSettingsEditor } from 'z3k-settings/provider/types';
import { heating_id_2_external_thermostat_id } from 'console/climatic/constants';
const control_mode_options = (current_value) => removeNulls([
    { text: 'Теплоноситель', value: HeatingCircuit.ControlMode.WATER },
    current_value == HeatingCircuit.ControlMode.EXT_THERMOSTAT
        ? {
            text: 'Термостат',
            value: HeatingCircuit.ControlMode.EXT_THERMOSTAT,
        }
        : null,
    { text: 'Воздух', value: HeatingCircuit.ControlMode.AIR },
    { text: 'ПЗА', value: HeatingCircuit.ControlMode.PZA },
]);
export function ExtendedSettings() {
    const { circuit, dhw_parallel_heating, control_mode, firmware_version, no_stop_heating_when_pid_is_in_low_threshold, heat_request, antileg_enabled, antileg_time, antileg_days, pza, } = useHeatingCircuitsItemState();
    const { replace_config, config } = useContext(Z3KSettingsEditor);
    const field = useCallback((input_props) => (_jsx(MaterialDropdown, { ...input_props, options: heatRequestOptions(heat_request) })), [heat_request, heatRequestOptions]);
    const hysteresisEditingProps = useEditingProps(circuit.id, 'heating_circuits', 'hysteresis');
    const heatRequestEditingProps = useEditingProps(circuit.id, 'heating_circuits', 'heat_request');
    const turnOffDelayEditingProps = useEditingProps(circuit.id, 'heating_circuits', 'turn_off_delay');
    const waterMinTemperatureEditingProps = useEditingProps(circuit.id, 'heating_circuits', 'water_min_temperature');
    const waterMaxTemperatureEditingProps = useEditingProps(circuit.id, 'heating_circuits', 'water_max_temperature');
    const hysteresisValidation = hysteresisEditingProps.value > 10 || hysteresisEditingProps.value < 0.1
        ? 'Допустимые значения: от 0.1 до 10'
        : null;
    return (_jsxs(SettingCard, { title: "\u0420\u0430\u0441\u0448\u0438\u0440\u0435\u043D\u043D\u044B\u0435 \u043D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438", children: [HeatingCircuit.isDHW(circuit) && (_jsx(MaterialBoolControl, { label: "\u041F\u0430\u0440\u0430\u043B\u043B\u0435\u043B\u044C\u043D\u044B\u0439 \u043D\u0430\u0433\u0440\u0435\u0432", value: dhw_parallel_heating, onChange: (checked) => {
                    replace_config({
                        ...config,
                        heating_circuits: config.heating_circuits.map((circuit) => {
                            if (circuit.type == HeatingCircuit.Type.HEATING) {
                                return HeatingCircuit.setBlockedWhileDHW(circuit, !checked);
                            }
                            return circuit;
                        }),
                    });
                } })), HeatingCircuit.isConsumer(circuit) && (_jsx(Control, { label: "\u0422\u0438\u043F \u0443\u043F\u0440\u0430\u0432\u043B\u0435\u043D\u0438\u044F", validation_result: null, validation_warnings_result: null, value: control_mode, onChange: (value) => {
                    switch (value) {
                        case HeatingCircuit.ControlMode.EXT_THERMOSTAT:
                            replace_config({
                                ...config,
                                heating_circuits: config.heating_circuits.map((item) => {
                                    if (item.id === circuit.id) {
                                        return {
                                            ...HeatingCircuit.setConsumerMode(item, HeatingCircuit.ConsumerMode.AIR),
                                            pza: 255,
                                            external_thermostat: heating_id_2_external_thermostat_id(circuit.id) ?? null,
                                        };
                                    }
                                    return item;
                                }),
                            });
                            break;
                        case HeatingCircuit.ControlMode.PZA:
                            replace_config({
                                ...config,
                                heating_circuits: config.heating_circuits.map((item) => {
                                    if (item.id === circuit.id) {
                                        return {
                                            ...HeatingCircuit.setConsumerMode(item, HeatingCircuit.ConsumerMode
                                                .WATER),
                                            external_thermostat: null,
                                            pza: parseFloatWithComma(pza) ?? 0,
                                        };
                                    }
                                    return item;
                                }),
                            });
                            break;
                        case HeatingCircuit.ControlMode.WATER:
                            replace_config({
                                ...config,
                                heating_circuits: config.heating_circuits.map((item) => {
                                    if (item.id === circuit.id) {
                                        return {
                                            ...HeatingCircuit.setConsumerMode(item, HeatingCircuit.ConsumerMode
                                                .WATER),
                                            external_thermostat: null,
                                            pza: 255,
                                        };
                                    }
                                    return item;
                                }),
                            });
                            break;
                        case HeatingCircuit.ControlMode.AIR:
                            replace_config({
                                ...config,
                                heating_circuits: config.heating_circuits.map((item) => {
                                    if (item.id === circuit.id) {
                                        return {
                                            ...HeatingCircuit.setConsumerMode(item, HeatingCircuit.ConsumerMode
                                                .AIR_PID),
                                            external_thermostat: null,
                                            pza: 255,
                                        };
                                    }
                                    return item;
                                }),
                            });
                            break;
                        default:
                            throw 'Unknown HeatingCircuit.ControlMode in circuit_tab::state2data';
                    }
                }, field: (input_props) => (_jsx(MaterialDropdown, { options: control_mode_options(control_mode ?? -1), ...input_props })) })), (HeatingCircuit.isConsumer(circuit) ||
                HeatingCircuit.isCooling(circuit)) &&
                HeatingCircuit.isAirPIDConsumerMode(circuit) &&
                firmware_version &&
                firmware_version >= 339 && (_jsx(MaterialBoolControl, { value: no_stop_heating_when_pid_is_in_low_threshold, onChange: (value) => {
                    replace_config({
                        ...config,
                        heating_circuits: config.heating_circuits.map((item) => {
                            if (item.id === circuit.id) {
                                return HeatingCircuit.setNoStopHeatingWhenPidIsInLowThreshold(item, value);
                            }
                            return item;
                        }),
                    });
                }, label: "C \u043A\u043E\u043D\u0442\u0440\u043E\u043B\u0435\u043C t\u00B0 \u0422\u041D \u043F\u043E \u043D\u0438\u0436\u043D\u0435\u0439 \u0433\u0440\u0430\u043D\u0438\u0446\u0435 \u043A\u043E\u043D\u0442\u0443\u0440\u0430", help_id: "heating_circuits_no_stop_heating_when_pid_is_in_low_threshold" })), _jsx(Control, { label: "\u0413\u0438\u0441\u0442\u0435\u0440\u0435\u0437\u0438\u0441", ...hysteresisEditingProps, validation_result: hysteresisValidation, field: (input_props) => (_jsx(StrictNumberInput, { min: 0.1, max: 10.0, step: 0.1, ...input_props })) }), HeatingCircuit.isConsumer(circuit) && (_jsx(Control, { label: "\u0417\u0430\u043F\u0440\u043E\u0441 \u043D\u0430 \u0442\u0435\u043F\u043B\u043E", ...heatRequestEditingProps, field: field })), HeatingCircuit.isConsumer(circuit) && (_jsx(Control, { label: "\u0417\u0430\u0434\u0435\u0440\u0436\u043A\u0430 \u0432\u044B\u043A\u043B\u044E\u0447\u0435\u043D\u0438\u044F \u043D\u0430\u0433\u0440\u0435\u0432\u0430", ...turnOffDelayEditingProps, field: (input_props) => (_jsx(StrictNumberInput, { min: 0, max: 120, ...input_props })) })), HeatingCircuit.isConsumer(circuit) && (_jsxs(SettingFullRow, { children: [_jsx(Control, { label: "\u041C\u0438\u043D. t\u00B0 \u0442\u0435\u043F\u043B\u043E\u043D\u043E\u0441\u0438\u0442\u0435\u043B\u044F", ...waterMinTemperatureEditingProps, value: waterMinTemperatureEditingProps.value, field: (input_props) => (_jsx(NullableNumberInput, { min: 5, max: 85, step: 1, ...input_props })) }), _jsx(Control, { label: "\u041C\u0430\u043A\u0441. t\u00B0 \u0442\u0435\u043F\u043B\u043E\u043D\u043E\u0441\u0438\u0442\u0435\u043B\u044F", ...waterMaxTemperatureEditingProps, value: waterMaxTemperatureEditingProps.value, field: (input_props) => (_jsx(NullableNumberInput, { min: 5, max: 85, step: 1, ...input_props })) })] })), HeatingCircuit.isDHW(circuit) && (_jsxs(_Fragment, { children: [_jsx(MaterialBoolControl, { label: "\u0410\u043D\u0442\u0438\u043B\u0435\u0433\u0438\u043E\u043D\u0435\u043B\u043B\u0430", value: antileg_enabled, onChange: (checked) => {
                            replace_config({
                                ...config,
                                heating_circuits: config.heating_circuits.map((item) => {
                                    if (item.id === circuit.id) {
                                        return HeatingCircuit.setAntilegEnabled(item, checked);
                                    }
                                    return item;
                                }),
                            });
                        } }), antileg_enabled && (_jsxs(_Fragment, { children: [_jsx(Control, { label: "\u0412\u0440\u0435\u043C\u044F \u0432\u043A\u043B\u044E\u0447\u0435\u043D\u0438\u044F", validation_result: null, validation_warnings_result: null, value: antileg_time, onChange: (value) => {
                                    replace_config({
                                        ...config,
                                        heating_circuits: config.heating_circuits.map((item) => {
                                            if (item.id === circuit.id) {
                                                return {
                                                    ...item,
                                                    antileg_mode_options: {
                                                        ...item.antileg_mode_options,
                                                        h: value.hour || 0,
                                                        m: value.min || 0,
                                                    },
                                                };
                                            }
                                            return item;
                                        }),
                                    });
                                }, field: TimeFieldInput }), _jsx(SettingFullRow, { children: _jsx(WeekdaysToggles, { bitmap: antileg_days, onChangeBitmap: (bitmap) => {
                                        replace_config({
                                            ...config,
                                            heating_circuits: config.heating_circuits.map((item) => {
                                                if (item.id === circuit.id) {
                                                    return {
                                                        ...item,
                                                        antileg_mode_options: {
                                                            ...item.antileg_mode_options,
                                                            days: bitmap,
                                                        },
                                                    };
                                                }
                                                return item;
                                            }),
                                        });
                                    } }) })] }))] }))] }));
}
